import { useRef } from 'react';
import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import {
    useBookCta,
    useEnquireCta,
} from '~/modules/enquiry/hooks/use-page-ctas';
import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    CONTENT_CAP_PADDED,
    detailCta,
    TrackedWebsiteCta,
} from '~/ui-rocco/pages/detail-shared';
import {
    ACTION_CARD_ACTION_CLASS,
    ACTION_CARD_CLASS,
    ACTION_CARD_CONTACT_IMAGE,
    ACTION_CARD_WEBSITE_IMAGE,
    ACTION_CARDS_GRID_CLASS,
    ACTION_CARDS_HEADING_CLASS,
    ActionCardBody,
    hostOf,
    WebsitePeek,
} from '~/ui-rocco/pages/detail-shared/DetailActionCards';
import {
    ModalPeek,
    useGrowToModal,
} from '~/ui-rocco/pages/detail-shared/ModalPeek';
import { EnquiryMiniModal } from '~/ui-rocco/pages/detail-shared/EnquiryPeek';
import { useLogSectionView } from '~/ui-rocco/hooks/use-log-section-view';

/**
 * The two routes off the product, said properly.
 *
 * Both actions exist elsewhere on the page as pills in the CTA cluster, where
 * they are a label and nothing else. Here each gets a photograph and a
 * sentence saying what the reader gets for taking it — which is the whole
 * point of the section, and why it sits at the foot of the page rather than
 * competing with the cluster at the top.
 *
 * Each card drops out on its own: a supplier with no website, or one whose
 * enquiries are hidden for this audience. With neither, the section does not
 * render. A configured Book a meeting button joins the enquiry card's action,
 * beside Get in touch.
 *
 * White, not the page's grey. The cards are the section — on the page's own
 * ground there is nothing to tell the reader where it starts, and the band
 * reads as two photographs that happen to follow the downloads.
 */
export const ProductGetSpecificSection = ({
    mobileCardsOnly = false,
}: {
    /**
     * Below md, the two cards alone: no heading, and none of the section's
     * own top air, so the cards sit in the page's rhythm like its other
     * bands. Desktop is unchanged.
     */
    mobileCardsOnly?: boolean;
} = {}) => {
    const { product } = useLoaderData<typeof loader>();
    const enquire = useEnquireCta({
        source: 'ProductGetSpecific',
        fixedLabel: 'Get in touch',
    });
    const bookMeeting = useBookCta({ source: 'ProductGetSpecific' });
    const showEnquiryCTA = enquire.visible;

    const professional = product.Professional;
    const website = product.Website || professional.Website;
    const supplier = professional.Title || 'the supplier';
    // The same rendition the real gate's pane resolves (the page's enquiry
    // brandImage): the miniature grows into the
    // modal, and a different crop of the same photograph re-framed itself at
    // the landing.
    const productImage =
        product.LibraryImages?.[0]?.LibraryImageFile?.DetailPageSquare
            ?.Desktop ??
        product.LibraryImages?.[0]?.LibraryImageFile?.PhotoTile ??
        undefined;

    // The product's own photographs ground the two cards, starting at the
    // second: the first is the one the gallery leads with and the peek circle
    // repeats, so taking it here would show the same picture three times on one
    // page. The supplier's profile banners top the pool up where a product has
    // only one or two photographs, and the bundled frames catch a product with
    // neither — the section would otherwise be imageless.
    const productPhotographs = (product.LibraryImages ?? [])
        .map((image) => image?.LibraryImageFile?.DetailPageNatural?.Desktop)
        .filter((src): src is string => !!src);
    const supplierBanners = (professional.Banners?.edges ?? [])
        .map(
            (edge) =>
                edge?.node?.LibraryImageFile?.DetailBannerStandard?.Desktop,
        )
        .filter((src): src is string => !!src);
    // One pool drawn from in order, so the two cards can never land on the
    // same picture as each other.
    const cardImagePool = [...productPhotographs.slice(1), ...supplierBanners];
    const websiteCardImage = cardImagePool[0] ?? ACTION_CARD_WEBSITE_IMAGE;
    const contactCardImage = cardImagePool[1] ?? ACTION_CARD_CONTACT_IMAGE;

    // The contact card's hover is the enquiry form it opens, small, and the
    // click grows it into the real one — the project page's enquiry card.
    const enquiryPeekRef = useRef<HTMLDivElement>(null);
    const enquiryMini = (
        <EnquiryMiniModal
            subject={{
                image: productImage,
                practice: supplier,
                note: professional.CustomEnquiryMessage,
                itemType: 'product',
            }}
        />
    );
    const enquiryFlight = useGrowToModal(enquiryPeekRef, enquiryMini);

    const cardsRef = useRef<HTMLDivElement>(null);
    useLogSectionView({
        ref: cardsRef,
        event: 'DetailActionCardsView',
        source: 'ProductGetSpecific',
    });

    const openEnquiry = () => enquiryFlight.grow(() => enquire.open());
    const enquireAction = (
        <span
            className={cn(detailCta('white-lifted'), ACTION_CARD_ACTION_CLASS)}
        >
            {enquire.label}
        </span>
    );
    const contactCardBody = (
        <ActionCardBody
            image={contactCardImage}
            hoverTint
            peek={<ModalPeek boxRef={enquiryPeekRef}>{enquiryMini}</ModalPeek>}
            copy={
                <>
                    Get in touch with {supplier} about pricing, lead times and
                    samples, or to book a consultation.
                </>
            }
            action={
                bookMeeting.inline ? (
                    <span className="relative flex flex-wrap gap-2">
                        {enquireAction}
                        {/* A real control, above the card's full-bleed
                            enquiry; the enquiry label beside it is drawn. */}
                        <button
                            type="button"
                            onClick={bookMeeting.open}
                            className={cn(
                                detailCta('white-lifted'),
                                'relative z-[2] w-fit',
                            )}
                        >
                            {bookMeeting.label}
                        </button>
                    </span>
                ) : (
                    enquireAction
                )
            }
        />
    );

    if (!website && !showEnquiryCTA) return null;

    return (
        // The air the reader sees, not the padding this section owns. Above
        // the heading there is already 120px of grey from the downloads band,
        // and below the cards only 60px before More products — so an equal
        // 181px top and bottom left the section sitting low in its own space.
        // 120 + 120 above against 181 + 60 below reads even.
        <section
            className={cn(
                'w-full bg-ground max-md:px-6 max-md:pt-10 max-md:pb-16 md:px-[3.125rem] md:pt-[7.5rem] md:pb-[11.3125rem]',
                mobileCardsOnly && 'max-md:pt-0 max-md:pb-10',
            )}
        >
            <div className={CONTENT_CAP_PADDED}>
                <h2
                    className={cn(
                        ACTION_CARDS_HEADING_CLASS,
                        'text-[#181818]',
                        mobileCardsOnly && 'max-md:hidden',
                    )}
                >
                    Get more Specific.
                </h2>

                <div
                    ref={cardsRef}
                    className={cn(
                        ACTION_CARDS_GRID_CLASS,
                        mobileCardsOnly && 'max-md:mt-0',
                    )}
                >
                    {website ? (
                        <TrackedWebsiteCta
                            asLink
                            website={website}
                            libraryItemID={product.ID}
                            professionalID={professional.ID}
                            professionalTitle={professional.Title}
                            source="ProductGetSpecific"
                            className={ACTION_CARD_CLASS}
                        >
                            <ActionCardBody
                                image={websiteCardImage}
                                peek={
                                    <WebsitePeek
                                        supplier={supplier}
                                        host={hostOf(website)}
                                    />
                                }
                                copy={
                                    <>
                                        Visit the {supplier} website for the
                                        full range, specification detail and
                                        where to buy this product.
                                    </>
                                }
                                action={
                                    <span
                                        className={cn(
                                            detailCta('white-lifted'),
                                            ACTION_CARD_ACTION_CLASS,
                                        )}
                                    >
                                        Visit Website
                                    </span>
                                }
                            />
                        </TrackedWebsiteCta>
                    ) : null}

                    {showEnquiryCTA && bookMeeting.inline ? (
                        // Two controls on one card can't nest, so the card's
                        // enquiry is a full-bleed button over the photograph
                        // and Book a meeting sits above it.
                        <div className={ACTION_CARD_CLASS}>
                            {contactCardBody}
                            <button
                                type="button"
                                aria-label={enquire.label}
                                onClick={openEnquiry}
                                className="absolute inset-0 z-[1] cursor-pointer bg-transparent p-0"
                            />
                        </div>
                    ) : showEnquiryCTA ? (
                        <button
                            type="button"
                            onClick={openEnquiry}
                            className={ACTION_CARD_CLASS}
                        >
                            {contactCardBody}
                        </button>
                    ) : null}
                </div>
            </div>
            {enquiryFlight.element}
        </section>
    );
};
